import { useMemo, useState } from 'react';
import { App } from 'antd';
import type { TableProps } from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import { Badge, Button, Dialog, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { QuanLyTichHopDialog } from '../components/QuanLyTichHopDialog';
import { TichHopFormDialog } from '../components/TichHopFormDialog';
import { NGUON_TICH_HOP, NHAN_NGUON, TONG_NGUON, laNguonBiet } from '../constants';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import { useCapLaiKhoa, useLuuTichHop, useTichHop, useXoaTichHop } from '../hooks/useVanBan';
import type { TichHop, TichHopKemKhoa } from '../types';

/** Cấu hình đầu vào xử lý văn bản (`/xu-ly-van-ban/cau-hinh/dau-vao`), chuyển từ foxainative. */
export function CauHinhDauVaoView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const { modal } = App.useApp();
  const { data, isLoading, error, refetch } = useTichHop();
  const luu = useLuuTichHop();
  const capLai = useCapLaiKhoa();
  const xoa = useXoaTichHop();

  const { canCreate, canUpdate, canDelete, canRotateKey } = useQuyenVanBan('integration');

  const [tuKhoa, setTuKhoa] = useState('');
  const [nguon, setNguon] = useState<string | undefined>();
  const [moForm, setMoForm] = useState(false);
  const [dangSua, setDangSua] = useState<TichHop | null>(null);
  const [quanLyId, setQuanLyId] = useState<string | null>(null);
  const [khoaMoi, setKhoaMoi] = useState<{ ten: string; khoa: string } | null>(null);

  const tatCa = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [data],
  );
  const rows = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return tatCa.filter((r) => (!q || r.name.toLowerCase().includes(q)) && (!nguon || r.source_label === nguon));
  }, [tatCa, tuKhoa, nguon]);
  const quanLy = tatCa.find((r) => r.id === quanLyId) ?? null;
  const coLoc = Boolean(tuKhoa.trim() || nguon);
  const so = (n: number) => soVN(n, locale);
  const nhanNguon = (s: string) => (laNguonBiet(s) ? t(NHAN_NGUON[s]) : s);

  const saoChep = (chu: string, khoa: KhoaDich) => {
    if (!navigator.clipboard) {
      toast.error(t('vanBanTichHop.khongSaoChep'));
      return;
    }
    navigator.clipboard
      .writeText(chu)
      .then(() => toast.success(t(khoa)))
      .catch(() => toast.error(t('vanBanTichHop.khongSaoChep')));
  };

  const moThem = () => {
    luu.reset();
    setDangSua(null);
    setMoForm(true);
  };
  const moSua = (r: TichHop) => {
    luu.reset();
    setQuanLyId(null);
    setDangSua(r);
    setMoForm(true);
  };
  const hoiXoa = (r: TichHop) =>
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanTichHop.xacNhanXoa', { ten: r.name }),
      content: t('vanBanTichHop.xacNhanXoaMo'),
      okText: t('vanBanTichHop.xoa'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: () =>
        xoa.mutateAsync(r.id).then(() => {
          if (quanLyId === r.id) setQuanLyId(null);
        }),
    });

  const columns: TableProps<TichHop>['columns'] = [
    {
      title: t('chung.stt'),
      width: 60,
      render: (_v, _r, i) => <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>,
    },
    {
      title: t('vanBanTichHop.cot.ten'),
      width: 300,
      render: (_v, r) => (
        <div className="min-w-0">
          <div className="font-medium text-content-primary">{r.name}</div>
          <div className="mt-0.5 flex items-center gap-1.5 font-mono text-xs text-content-muted">
            <KeyOutlined /> {r.api_key_prefix}…
          </div>
        </div>
      ),
    },
    {
      title: t('vanBanTichHop.cot.nguon'),
      dataIndex: 'source_label',
      width: 180,
      render: (s: string) => (
        <Badge variant={laNguonBiet(s) ? TONG_NGUON[s] : 'neutral'}>{nhanNguon(s)}</Badge>
      ),
    },
    {
      title: t('vanBanTichHop.cot.webhook'),
      width: 300,
      render: (_v, r) =>
        r.webhook_url ? (
          <div className="flex min-w-0 max-w-64 items-center gap-1.5 text-xs text-content-secondary" title={r.webhook_url}>
            <Badge variant="neutral">{r.webhook_method}</Badge>
            <span className="truncate">{r.webhook_url}</span>
          </div>
        ) : (
          <span className="text-xs text-content-muted">{t('vanBanTichHop.chuaCauHinh')}</span>
        ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'is_active',
      width: 140,
      render: (a: boolean) =>
        a ? (
          <Badge variant="success">{t('vanBanTichHop.dangHoatDong')}</Badge>
        ) : (
          <Badge variant="neutral">{t('vanBanTichHop.daTat')}</Badge>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      render: (_v, r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MenuThaoTac
            tenBanGhi={r.name}
            muc={[
              { khoa: 'quan-ly', nhan: t('vanBanTichHop.quanLy'), icon: <SettingOutlined className="text-violet-500" />, nhom: 'xem', onChon: () => setQuanLyId(r.id) },
              { khoa: 'sua', nhan: t('vanBanTichHop.sua'), icon: <EditOutlined className="text-primary-600" />, hien: canUpdate, nhom: 'sua', onChon: () => moSua(r) },
              { khoa: 'xoa', nhan: t('vanBanTichHop.xoa'), icon: <DeleteOutlined />, hien: canDelete, nhom: 'pha-huy', onChon: () => hoiXoa(r) },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.xuLyVanBan') },
            { label: t('nav.muc.cauHinhXuLyVanBan') },
            { label: t('nav.muc.cauHinhDauVaoVb') },
          ]}
          titleIcon={<ApiOutlined className="text-primary-600" />}
          title={t('vanBanTichHop.tieuDe')}
          subtitle={t('vanBanTichHop.moTa')}
          actions={
            <>
              <Button variant="ghost" title={t('chung.lamMoi')} aria-label={t('chung.lamMoi')} onClick={() => void refetch()}>
                <ReloadOutlined className={isLoading ? 'animate-spin' : ''} />
              </Button>
              {canCreate && (
                <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                  {t('vanBanTichHop.them')}
                </Button>
              )}
            </>
          }
        />

        <div className="grid grid-cols-12 gap-4">
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-4" nhan={t('vanBanTichHop.thongKe.tong')} so={so(tatCa.length)} icon={<ApiOutlined />} tone="primary" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-4" nhan={t('vanBanTichHop.thongKe.hoatDong')} so={so(tatCa.filter((r) => r.is_active).length)} icon={<CheckCircleOutlined />} tone="success" />
          <StatTile span="col-span-12 sm:col-span-6 lg:col-span-4" nhan={t('vanBanTichHop.thongKe.coWebhook')} so={so(tatCa.filter((r) => !!r.webhook_url).length)} icon={<SendOutlined />} tone="violet" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="min-w-[200px] max-w-sm flex-1"
            allowClear
            prefix={<SearchOutlined className="text-primary-500" />}
            placeholder={t('vanBanTichHop.timTheo')}
            aria-label={t('vanBanTichHop.timTheo')}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select
            allowClear
            className="min-w-[12rem]"
            placeholder={t('vanBanTichHop.tatCaNguon')}
            aria-label={t('vanBanTichHop.locNguon')}
            options={NGUON_TICH_HOP.map((v) => ({ value: v, label: t(NHAN_NGUON[v]) }))}
            value={nguon}
            onChange={(v) => setNguon(v as string | undefined)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {error ? (
            <div role="alert" className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-danger-700">{t('vanBanTichHop.loiDanhSach', { loi: dichLoi(error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <DataTable<TichHop>
              caption={t('vanBanTichHop.bangCaption')}
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={isLoading}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              onRow={(r) => ({ onClick: () => setQuanLyId(r.id), className: 'cursor-pointer' })}
              locale={{
                emptyText: isLoading ? (
                  ' '
                ) : (
                  <Empty
                    icon={ApiOutlined}
                    title={t(coLoc ? 'vanBanTichHop.rongLoc' : 'vanBanTichHop.rongThat')}
                    description={coLoc ? undefined : t('vanBanTichHop.rongThatMo')}
                    action={
                      coLoc || !canCreate ? undefined : (
                        <Button variant="primary" icon={<PlusOutlined />} onClick={moThem}>
                          {t('vanBanTichHop.them')}
                        </Button>
                      )
                    }
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      <TichHopFormDialog
        open={moForm}
        tichHop={dangSua}
        saving={luu.isPending}
        loiMayChu={luu.error}
        onClose={() => setMoForm(false)}
        onSubmit={(vao) =>
          luu.mutate(
            { id: dangSua?.id ?? null, vao },
            {
              onSuccess: (kq) => {
                setMoForm(false);
                // Tạo mới: hiện API key một lần thay cho toast
                if (!dangSua && 'api_key' in kq) setKhoaMoi({ ten: kq.name, khoa: (kq as TichHopKemKhoa).api_key });
              },
            },
          )
        }
      />

      <QuanLyTichHopDialog
        tichHop={quanLy}
        coTheSua={canUpdate}
        coTheXoa={canDelete}
        coTheCapLai={canRotateKey}
        dangCapLai={capLai.isPending}
        onClose={() => setQuanLyId(null)}
        onSua={moSua}
        onXoa={hoiXoa}
        onCapLai={(r) =>
          capLai.mutateAsync(r.id).then((kq) => {
            setQuanLyId(null);
            setKhoaMoi({ ten: kq.name, khoa: kq.api_key });
          })
        }
        onSaoChep={saoChep}
      />

      {/* API key chỉ hiện một lần, sau khi tạo mới hoặc cấp lại */}
      <Dialog
        open={!!khoaMoi}
        onClose={() => setKhoaMoi(null)}
        width={560}
        destroyOnHidden
        maskClosable={false}
        title={t('vanBanKhoa.tieuDe', { ten: khoaMoi?.ten ?? '' })}
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setKhoaMoi(null)}>
              {t('vanBanKhoa.daLuuKhoa')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3.5 py-2.5 text-xs text-warning-700">
            <ExclamationCircleOutlined className="mt-0.5 shrink-0" />
            {t('vanBanKhoa.canhBao')}
          </p>
          <div className="flex items-center gap-2 rounded border border-default bg-subtle px-3.5 py-2.5">
            <code className="flex-1 break-all font-mono text-sm text-content-primary">{khoaMoi?.khoa}</code>
            <Button
              variant="outline"
              size="sm"
              icon={<CopyOutlined />}
              onClick={() => khoaMoi && saoChep(khoaMoi.khoa, 'vanBanTichHop.daSaoChepKhoa')}
            >
              {t('vanBanKhoa.saoChep')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
